import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { normalizeVerticalValue, type Vertical } from '../../../core/enums/verticals';
import { pickFirstCompanyRoleValueFromRecords } from '../../../core/utils/taskeo-company-role-response.util';
import type { CompanyMembership, ResolvedCompanyRouteContext } from '../models/company.models';
import { normalizeCompanyMembershipRole } from '../utils/company-membership-role.util';

type JsonRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class CompanyApiService {
  private readonly http = inject(HttpClient);

  async getMemberships(): Promise<CompanyMembership[]> {
    const response = await this.getWithFallback([
      '/api/memberships',
      '/api/company-memberships',
      '/memberships',
    ]);
    const items = this.asArray(response).map((entry) => this.asRecord(entry));

    return items
      .map((entry) => this.mapMembershipEntry(entry))
      .filter((membership) => !!membership.companyId?.trim());
  }

  /** Resolve the route key using the API contract that validates the signed-in user's membership. */
  async resolveCompanyByKey(companyKey: string): Promise<ResolvedCompanyRouteContext> {
    const trimmed = companyKey.trim();
    const response = await firstValueFrom(
      this.http.get<unknown>('/api/companies/context/' + encodeURIComponent(trimmed)).pipe(timeout(8_000))
    );
    return this.mapResolvedCompanyContext(response, trimmed);
  }
  private mapMembershipEntry(entry: JsonRecord): CompanyMembership {
    const companyRec = this.asRecord(entry['company'] ?? entry['Company']);
    const tenantRec = this.asRecord(entry['tenant'] ?? entry['Tenant']);
    const tenantCompany = this.asRecord(tenantRec['company'] ?? tenantRec['Company']);
    const membershipRec = this.asRecord(entry['membership'] ?? entry['Membership']);
    const userMembershipRec = this.asRecord(entry['userMembership'] ?? entry['UserMembership']);
    // Ordem: nome fantasia primeiro (decisão de produto — switcher mostra o nome
    // de marca/operacional, não a razão social). Genéricos por último. companyName/name
    // caem pro fim porque já vimos backend devolvendo taxId nesses campos por bug de DTO.
    const nameKeys = [
      'tradeName',
      'TradeName',
      'displayName',
      'DisplayName',
      'legalName',
      'LegalName',
      'razaoSocial',
      'RazaoSocial',
      'companyName',
      'CompanyName',
      'tenantName',
      'TenantName',
      'name',
      'Name',
    ] as const;
    const companyName =
      this.readFirstCompanyDisplayName(entry, [...nameKeys]) ||
      this.readFirstCompanyDisplayName(companyRec, [...nameKeys]) ||
      this.readFirstCompanyDisplayName(tenantCompany, [...nameKeys]);

    const companyKey =
      this.pickCompanyKey(entry, companyRec, tenantCompany) ||
      (companyName ? this.slugify(companyName) : '');

    const roleRaw = this.pickMembershipRoleRaw(
      entry,
      companyRec,
      tenantCompany,
      membershipRec,
      userMembershipRec
    );
    let role = normalizeCompanyMembershipRole(roleRaw, 'Member');
    if (
      role === 'Member' &&
      this.hasElevatedCompanyMembership(entry, membershipRec, userMembershipRec, companyRec, tenantCompany)
    ) {
      role = 'Admin';
    }

    return {
      companyId:
        this.readFirstString(entry, ['companyId', 'CompanyId', 'tenantId', 'TenantId']) ||
        this.readFirstString(companyRec, ['id', 'Id', 'companyId', 'CompanyId']) ||
        this.readFirstString(tenantCompany, ['id', 'Id', 'companyId', 'CompanyId']),
      companyKey,
      companyName,
      companyLogo: this.pickMembershipLogo(entry, companyRec, tenantCompany),
      role,
      status: this.readFirstString(entry, ['status', 'Status']) || 'Active',
      vertical: this.pickMembershipVertical(entry, companyRec, tenantCompany),
    };
  }

  /**
   * Lê `vertical` em vários níveis do DTO (raiz, company, tenant.company).
   * Backend ASP.NET serializa enum como int — `normalizeVerticalValue` cobre
   * número e string. Retorna `undefined` quando o campo não vem em nenhum
   * registro (preserva semântica de "não-conhecido" usada pelos consumidores).
   */
  private pickMembershipVertical(
    ...records: JsonRecord[]
  ): Vertical | null | undefined {
    for (const rec of records) {
      if (!rec || !Object.keys(rec).length) continue;
      if ('vertical' in rec || 'Vertical' in rec) {
        return normalizeVerticalValue(rec['vertical'] ?? rec['Vertical']);
      }
    }
    return undefined;
  }

  /** Indicadores booleanos quando `role` vem vazio ou como objeto não mapeado. */
  private hasElevatedCompanyMembership(...records: JsonRecord[]): boolean {
    const keys = [
      'isCompanyAdmin',
      'IsCompanyAdmin',
      'isTenantAdmin',
      'IsTenantAdmin',
      'isAdmin',
      'IsAdmin',
      'canManageCompany',
      'CanManageCompany',
      'isAdministrator',
      'IsAdministrator',
    ];
    for (const rec of records) {
      if (!rec || !Object.keys(rec).length) continue;
      for (const k of keys) {
        const v = rec[k];
        if (v === true) return true;
        if (v === 1) return true;
        if (typeof v === 'string' && v.trim().toLowerCase() === 'true') return true;
      }
    }
    return false;
  }

  /** Lê logo em várias chaves comuns (APIs diferentes / casing .NET). */
  private pickMembershipLogo(entry: JsonRecord, companyRec: JsonRecord, tenantCompany: JsonRecord): string | null {
    const tenantRec = this.asRecord(entry['tenant'] ?? entry['Tenant']);
    const sources: JsonRecord[] = [entry, companyRec, tenantRec, tenantCompany];
    const keys = [
      'companyLogo',
      'companyLogoUrl',
      'logoUrl',
      'LogoUrl',
      'logo',
      'imageUrl',
      'brandLogoUrl',
    ];
    for (const rec of sources) {
      if (!rec || Object.keys(rec).length === 0) continue;
      for (const k of keys) {
        const v = this.asNullableString(rec[k]);
        if (v?.trim()) return v.trim();
      }
    }
    return null;
  }

  private pickMembershipRoleRaw(
    entry: JsonRecord,
    companyRec: JsonRecord,
    tenantCompany: JsonRecord,
    membershipRec: JsonRecord,
    userMembershipRec: JsonRecord
  ): unknown {
    /**
     * Ordem: vínculo empresa ↔ utilizador primeiro. O DTO raiz (`entry`) costuma misturar campos do
     * utilizador global (ex. `roleValue` de outra tabela) e rebaxava o papel na empresa para Member.
     */
    const sources: JsonRecord[] = [
      membershipRec,
      userMembershipRec,
      entry,
      companyRec,
      tenantCompany,
    ];
    const fromValue = pickFirstCompanyRoleValueFromRecords(sources);
    if (fromValue !== undefined) return fromValue;

    const keys = [
      'role',
      'Role',
      'membershipRole',
      'MembershipRole',
      'companyRole',
      'CompanyRole',
      'userRole',
      'UserRole',
    ];
    for (const source of sources) {
      if (!source || !Object.keys(source).length) continue;
      for (const key of keys) {
        const v = source[key];
        if (v !== undefined && v !== null && typeof v === 'object' && !Array.isArray(v)) {
          return v;
        }
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      const entries = Object.entries(source);
      for (const key of keys) {
        const lower = key.toLowerCase();
        const found = entries.find(([entryKey]) => entryKey.toLowerCase() === lower);
        const v = found?.[1];
        if (v !== undefined && v !== null && typeof v === 'object' && !Array.isArray(v)) {
          return v;
        }
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
    }
    return undefined;
  }

  private pickCompanyKey(entry: JsonRecord, companyRec: JsonRecord, tenantCompany: JsonRecord): string {
    const key =
      this.readFirstString(entry, ['companyKey', 'CompanyKey', 'key', 'Key', 'slug', 'Slug']) ||
      this.readFirstString(companyRec, ['companyKey', 'CompanyKey', 'key', 'Key', 'slug', 'Slug']) ||
      this.readFirstString(tenantCompany, ['companyKey', 'CompanyKey', 'key', 'Key', 'slug', 'Slug']);
    return key.trim();
  }

  private mapResolvedCompanyContext(raw: unknown, fallbackKey: string): ResolvedCompanyRouteContext {
    const root = this.asRecord(raw);
    const data = this.asRecord(
      root['data'] ?? root['Data'] ?? root['result'] ?? root['Result'] ?? root['payload'] ?? root['Payload'] ?? root
    );
    const company = this.asRecord(data['company'] ?? data['Company'] ?? data);

    const companyId =
      this.readFirstString(company, ['id', 'Id', 'companyId', 'CompanyId']) ||
      this.readFirstString(data, ['companyId', 'CompanyId', 'id', 'Id']) ||
      this.readFirstString(root, ['companyId', 'CompanyId', 'id', 'Id']);

    const companyKey =
      this.pickCompanyKey(data, company, {}) ||
      this.pickCompanyKey(root, company, {}) ||
      fallbackKey;

    // Ordem alinhada com `mapMembershipEntry` e `readCompanyNameFromResponse` (auth):
    // nome fantasia primeiro, depois razão social (LegalName) que é o nome operacional
    // no banco quando `tradeName` não foi preenchido — antes caía no
    // `prettifyRouteKey(fallbackKey)` que entrega slug capitalizada (ex.: "Stonetelemaco")
    // mesmo com legalName disponível no payload. `readFirstCompanyDisplayName` filtra
    // taxId (CPF/CNPJ) caso back devolva no campo nome por bug de DTO.
    const nameKeys = [
      'tradeName',
      'TradeName',
      'displayName',
      'DisplayName',
      'legalName',
      'LegalName',
      'razaoSocial',
      'RazaoSocial',
      'companyName',
      'CompanyName',
      'tenantName',
      'TenantName',
      'name',
      'Name',
    ];
    const name =
      this.readFirstCompanyDisplayName(company, nameKeys) ||
      this.readFirstCompanyDisplayName(data, nameKeys) ||
      this.readFirstCompanyDisplayName(root, nameKeys) ||
      this.prettifyRouteKey(fallbackKey);

    const logoRaw =
      this.readFirstString(company, ['logoUrl', 'LogoUrl', 'logo', 'Logo', 'assetUrl', 'AssetUrl']) ||
      this.readFirstString(data, ['logoUrl', 'LogoUrl']) ||
      this.readFirstString(root, ['logoUrl', 'LogoUrl']);
    const logoUrl = logoRaw || null;

    const roleValueFirst = pickFirstCompanyRoleValueFromRecords([data, company, root]);
    const roleRaw =
      roleValueFirst ??
      data['role'] ??
      data['Role'] ??
      company['role'] ??
      company['Role'] ??
      root['role'] ??
      root['Role'] ??
      (this.readFirstString(data, ['role', 'Role']) ||
        this.readFirstString(company, ['role', 'Role']) ||
        this.readFirstString(root, ['role', 'Role']));
    const role = normalizeCompanyMembershipRole(roleRaw);

    const hasAccessRaw = data['hasAccess'] ?? root['hasAccess'] ?? company['hasAccess'];
    const hasAccess = hasAccessRaw === undefined ? true : Boolean(hasAccessRaw);

    return {
      companyId,
      companyKey: companyKey.trim() || fallbackKey,
      name,
      logoUrl,
      role,
      hasAccess,
    };
  }

  private asRecord(value: unknown): JsonRecord {
    return value && typeof value === 'object' ? (value as JsonRecord) : {};
  }

  private asArray(value: unknown): unknown[] {
    if (Array.isArray(value)) return value;
    const source = this.asRecord(value);
    const directKeys = [
      'items',
      'Items',
      'data',
      'Data',
      'results',
      'Results',
      'value',
      'Value',
      'memberships',
      'Memberships',
    ];
    for (const key of directKeys) {
      const v = source[key];
      if (Array.isArray(v)) return v;
    }
    for (const key of directKeys) {
      const v = source[key];
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const inner = this.asRecord(v);
        const nested = inner['items'] ?? inner['Items'];
        if (Array.isArray(nested)) return nested;
      }
    }
    return [];
  }

  private asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
  }

  /**
   * Específico pro nome de exibição da empresa: aplica a mesma lógica do `readFirstString`
   * mas **descarta** valores que são CPF (11 dígitos) ou CNPJ (14 dígitos) puros — defesa
   * contra DTO do back devolvendo taxId no campo nome. Cai pro próximo candidato em vez
   * de mostrar 14 dígitos no switcher.
   */
  private readFirstCompanyDisplayName(source: JsonRecord, keys: string[]): string {
    if (!source || !Object.keys(source).length) return '';
    const isLikelyTaxId = (raw: string): boolean => {
      const digits = raw.replace(/\D/g, '');
      const onlyDigitsAndMaskChars = /^[\d./\-\s]+$/.test(raw);
      return onlyDigitsAndMaskChars && (digits.length === 11 || digits.length === 14);
    };
    for (const key of keys) {
      const direct = source[key];
      if (typeof direct === 'string' && direct.trim() && !isLikelyTaxId(direct)) {
        return direct.trim();
      }
    }
    const entries = Object.entries(source);
    for (const key of keys) {
      const lower = key.toLowerCase();
      const found = entries.find(([entryKey]) => entryKey.toLowerCase() === lower);
      if (found && typeof found[1] === 'string' && found[1].trim() && !isLikelyTaxId(found[1])) {
        return found[1].trim();
      }
    }
    return '';
  }

  /** Lê string da API .NET (PascalCase) e JSON camelCase sem duplicar cada chave. */
  private readFirstString(source: JsonRecord, keys: string[]): string {
    if (!source || !Object.keys(source).length) return '';
    for (const key of keys) {
      const direct = source[key];
      if (typeof direct === 'string' && direct.trim()) return direct.trim();
    }
    const entries = Object.entries(source);
    for (const key of keys) {
      const lower = key.toLowerCase();
      const found = entries.find(([entryKey]) => entryKey.toLowerCase() === lower);
      if (found && typeof found[1] === 'string' && found[1].trim()) {
        return found[1].trim();
      }
    }
    return '';
  }

  private prettifyRouteKey(key: string): string {
    const t = key.trim();
    if (!t) return '';
    return t
      .split(/[-_/]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private asNullableString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  private async getWithFallback(endpoints: string[]): Promise<unknown> {
    let lastError: unknown;
    for (const endpoint of endpoints) {
      try {
        return await firstValueFrom(
          this.http.get<unknown>(endpoint).pipe(timeout(8_000))
        );
      } catch (error) {
          if (!(error instanceof HttpErrorResponse) || error.status !== 404) throw error;
        lastError = error;
      }
    }
    throw lastError ?? new Error('Request failed');
  }
}
